import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { notify } from "./Notification";
import useClock from "../hooks/useClock";
import useNotifications, { notifications } from "../hooks/useNotifications";
import useWeather from "../hooks/useWeather";
import { resume } from "../data/resume";
import messagesIcon from "../assets/icons/ios/messages.png";
import { ReactComponent as XMark } from "../assets/svgs/sf-symbols/notification-center/xmark.svg";
import { ReactComponent as Location } from "../assets/svgs/sf-symbols/notification-center/location.fill.svg";
import { ReactComponent as Folder } from "../assets/svgs/sf-symbols/spotlight/folder.svg";
import { ReactComponent as Sun } from "../assets/svgs/sf-symbols/notification-center/sun.max.fill.svg";
import { ReactComponent as Moon } from "../assets/svgs/sf-symbols/notification-center/moon.stars.fill.svg";
import { ReactComponent as CloudSun } from "../assets/svgs/sf-symbols/notification-center/cloud.sun.fill.svg";
import { ReactComponent as CloudMoon } from "../assets/svgs/sf-symbols/notification-center/cloud.moon.fill.svg";
import { ReactComponent as Cloud } from "../assets/svgs/sf-symbols/notification-center/cloud.fill.svg";
import { ReactComponent as Fog } from "../assets/svgs/sf-symbols/notification-center/cloud.fog.fill.svg";
import { ReactComponent as Drizzle } from "../assets/svgs/sf-symbols/notification-center/cloud.drizzle.fill.svg";
import { ReactComponent as Rain } from "../assets/svgs/sf-symbols/notification-center/cloud.rain.fill.svg";
import { ReactComponent as HeavyRain } from "../assets/svgs/sf-symbols/notification-center/cloud.heavyrain.fill.svg";
import { ReactComponent as Snow } from "../assets/svgs/sf-symbols/notification-center/cloud.snow.fill.svg";
import { ReactComponent as Storm } from "../assets/svgs/sf-symbols/notification-center/cloud.bolt.rain.fill.svg";
import { ReactComponent as Sunrise } from "../assets/svgs/sf-symbols/notification-center/sunrise.fill.svg";
import { ReactComponent as Sunset } from "../assets/svgs/sf-symbols/notification-center/sunset.fill.svg";

const WEATHER_ICONS = {
  sun: Sun,
  moon: Moon,
  cloudSun: CloudSun,
  cloudMoon: CloudMoon,
  cloud: Cloud,
  fog: Fog,
  drizzle: Drizzle,
  rain: Rain,
  heavyRain: HeavyRain,
  snow: Snow,
  storm: Storm,
  sunrise: Sunrise,
  sunset: Sunset,
};

const CLOCKS = [
  { city: "Accra", timeZone: "Africa/Accra" },
  { city: "Toronto", timeZone: "America/Toronto" },
  { city: "London", timeZone: "Europe/London" },
  { city: "Cupertino", timeZone: "America/Los_Angeles" },
];

const openResume = () => window.dispatchEvent(new CustomEvent("open-resume", { detail: {} }));
const contact = (label) => resume.contacts.find((c) => c.label === label);

/* ---------- Notifications ---------- */

const Card = ({ item, onOpen, clamped, clearsAll }) => (
  <div className="nc-card-wrap">
    <button type="button" className="nc-card" onClick={onOpen}>
      <img src={messagesIcon} alt="" className="nc-card-icon" />
      <span className="min-w-0 flex-1">
        <span className="nc-card-title">{item.title}</span>
        <span className={`nc-card-body ${clamped ? "is-clamped" : ""}`}>{item.body}</span>
      </span>
    </button>
    <button
      type="button"
      className="nc-card-clear"
      onClick={() => (clearsAll ? notifications.clear() : notifications.remove(item.id))}
      aria-label={clearsAll ? "Clear all notifications" : "Clear notification"}
    >
      <XMark />
    </button>
  </div>
);

// Messages notifications as one stack, as macOS groups them by app. Click to fan it out.
const NotificationStack = ({ items, open, onClose }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open || items.length < 2) setExpanded(false);
  }, [open, items.length]);

  if (!items.length) return null;

  const stacked = !expanded && items.length > 1;
  const openFromCard = () => {
    if (stacked) {
      setExpanded(true);
      return;
    }
    onClose();
    openResume();
  };

  const peeks = stacked ? Math.min(items.length - 1, 2) : 0;

  return (
    <div className={`nc-stack nc-item has-${peeks}-peeks`} style={{ "--i": 1 }}>
      {expanded && (
        <div className="nc-stack-head">
          <button type="button" className="nc-pill" onClick={() => setExpanded(false)}>
            Show Less
          </button>
        </div>
      )}
      {(expanded ? items : items.slice(0, 1)).map((item) => (
        <Card key={item.id} item={item} onOpen={openFromCard} clamped={!expanded} clearsAll={stacked} />
      ))}
      {peeks > 0 && <span className="nc-peek nc-peek--1" aria-hidden="true" />}
      {peeks > 1 && <span className="nc-peek nc-peek--2" aria-hidden="true" />}
    </div>
  );
};

/* ---------- Widgets (full-color, as macOS shows them by default) ---------- */

const WeatherWidget = ({ open }) => {
  const { status, weather } = useWeather(open);
  const city = (
    <p className="nc-weather-city">
      Accra <Location className="nc-location" />
    </p>
  );
  if (status !== "ready") {
    return (
      <div className="nc-widget nc-w-medium nc-item nc-weather sky-cloudy" style={{ "--i": 2 }}>
        {city}
        <p className="nc-weather-note">
          {status === "loading" ? "Loading weather…" : "Weather isn't available right now."}
        </p>
      </div>
    );
  }
  const Icon = WEATHER_ICONS[weather.icon];
  return (
    <div className={`nc-widget nc-w-medium nc-item nc-weather sky-${weather.sky}`} style={{ "--i": 2 }}>
      <div className="flex justify-between">
        <div>
          {city}
          <p className="nc-weather-temp">{weather.temp}°</p>
        </div>
        <div className="nc-weather-summary">
          <Icon className="nc-weather-icon" aria-hidden="true" />
          <p>{weather.text}</p>
          <p>
            H:{weather.high}° L:{weather.low}°
          </p>
        </div>
      </div>
      <ol className="nc-weather-hours">
        {weather.hourly.map((h) => {
          const HourIcon = WEATHER_ICONS[h.icon];
          return (
            <li key={h.time}>
              <span className="nc-weather-hour">{h.label}</span>
              <HourIcon className="nc-weather-hour-icon" aria-label={h.text} />
              <span>{h.temp}°</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// Wall-clock time in a time zone.
const partsIn = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(date);
  const p = Object.fromEntries(parts.map((x) => [x.type, Number(x.value)]));
  return { y: p.year, mo: p.month, d: p.day, h: p.hour % 24, m: p.minute, s: p.second };
};

// Analog face, white by day and dark at night, as Apple's World Clock draws it.
const ClockFace = ({ h, m, s, day }) => (
  <svg viewBox="-50 -50 100 100" className={`nc-clock ${day ? "is-day" : "is-night"}`} aria-hidden="true">
    <circle r="49" className="nc-clock-face" />
    {Array.from({ length: 12 }, (_, i) => {
      const a = ((i + 1) * Math.PI) / 6;
      return (
        <text key={i} x={Math.sin(a) * 37} y={-Math.cos(a) * 37 + 4} textAnchor="middle" className="nc-clock-num">
          {i + 1}
        </text>
      );
    })}
    <line y1="5" y2="-22" className="nc-clock-hour" transform={`rotate(${(h % 12) * 30 + m / 2})`} />
    <line y1="7" y2="-37" className="nc-clock-minute" transform={`rotate(${m * 6 + s / 10})`} />
    <line y1="10" y2="-41" className="nc-clock-second" transform={`rotate(${s * 6})`} />
    <circle r="3.2" className="nc-clock-pin" />
  </svg>
);

const WorldClockWidget = () => {
  const now = useClock(1000);
  const local = {
    y: now.getFullYear(),
    mo: now.getMonth() + 1,
    d: now.getDate(),
    h: now.getHours(),
    m: now.getMinutes(),
  };
  const localStamp = Date.UTC(local.y, local.mo - 1, local.d, local.h, local.m);
  return (
    <div className="nc-widget nc-w-medium nc-item nc-world" style={{ "--i": 3 }}>
      {CLOCKS.map(({ city, timeZone }) => {
        const t = partsIn(now, timeZone);
        const stamp = Date.UTC(t.y, t.mo - 1, t.d, t.h, t.m);
        const hours = Math.round((stamp - localStamp) / 360000) / 10;
        const dayDiff = Date.UTC(t.y, t.mo - 1, t.d) - Date.UTC(local.y, local.mo - 1, local.d);
        const dayLabel = dayDiff > 0 ? "Tomorrow" : dayDiff < 0 ? "Yesterday" : "Today";
        const offset = `${hours >= 0 ? "+" : "-"}${Math.abs(hours)}${Math.abs(hours) === 1 ? "HR" : "HRS"}`;
        return (
          <div key={city} className="nc-world-clock">
            <ClockFace h={t.h} m={t.m} s={t.s} day={t.h >= 6 && t.h < 18} />
            <p className="nc-world-city">{city}</p>
            <p className="nc-world-meta">{dayLabel}</p>
            <p className="nc-world-meta">{offset}</p>
          </div>
        );
      })}
    </div>
  );
};

// "9–9:15AM", "11:45AM–12:15PM", as Calendar writes ranges.
const formatRange = (start, end) => {
  const part = (d) =>
    `${d.getHours() % 12 || 12}${d.getMinutes() ? `:${String(d.getMinutes()).padStart(2, "0")}` : ""}`;
  const mer = (d) => (d.getHours() < 12 ? "AM" : "PM");
  return mer(start) === mer(end)
    ? `${part(start)}–${part(end)}${mer(end)}`
    : `${part(start)}${mer(start)}–${part(end)}${mer(end)}`;
};

const at = (dayOffset, h, m = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return d;
};

// "Up Next": today on the left, tomorrow on the right. The events are invitations.
const CalendarWidget = ({ onClose }) => {
  const now = useClock(60000);
  // Today's event starts at the next quarter hour.
  const start = new Date(now);
  start.setMinutes(Math.ceil((now.getMinutes() + 1) / 15) * 15, 0, 0);
  const end = new Date(start.getTime() + 30 * 60000);
  const email = contact("Email");
  const linkedin = contact("LinkedIn");

  const go = (run) => () => {
    onClose();
    run();
  };
  const today = [{ title: "Read Sylvester's resume", start, end, color: "teal", run: go(openResume) }];
  const tomorrow = [
    {
      title: "Email Sylvester",
      start: at(1, 9),
      end: at(1, 9, 15),
      color: "teal",
      run: go(() => window.open(email.href)),
    },
    {
      title: "Intro call with Sylvester",
      start: at(1, 10),
      end: at(1, 10, 30),
      color: "purple",
      run: go(() => window.open(email.href)),
    },
    {
      title: "Connect on LinkedIn",
      start: at(1, 14),
      end: at(1, 14, 15),
      color: "orange",
      run: go(() => window.open(linkedin.href, "_blank", "noopener")),
    },
  ];

  const Event = ({ e }) => (
    <button type="button" onClick={e.run} className={`nc-event is-${e.color}`}>
      <span className="nc-event-title">{e.title}</span>
      <span className="nc-event-time">{formatRange(e.start, e.end)}</span>
    </button>
  );

  return (
    <div className="nc-widget nc-w-medium nc-item nc-calendar" style={{ "--i": 4 }}>
      <div className="nc-calendar-col">
        <p className="nc-calendar-weekday">{now.toLocaleString("en-US", { weekday: "long" })}</p>
        <p className="nc-calendar-date">{now.getDate()}</p>
        {today.map((e) => (
          <Event key={e.title} e={e} />
        ))}
      </div>
      <div className="nc-calendar-col">
        <p className="nc-calendar-tomorrow">Tomorrow</p>
        {tomorrow.map((e) => (
          <Event key={e.title} e={e} />
        ))}
      </div>
    </div>
  );
};

const NotesWidget = ({ onClose }) => {
  const now = useClock(60000);
  const date = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
  return (
    <button
      type="button"
      onClick={() => {
        onClose();
        openResume();
      }}
      className="nc-widget nc-w-small nc-item nc-notes"
      style={{ "--i": 5 }}
      aria-label="Open my resume in Notes"
    >
      <span className="nc-notes-head">
        <Folder className="nc-notes-folder" aria-hidden="true" />
        Notes
      </span>
      <span className="nc-notes-body">
        <span className="nc-notes-title">My resume</span>
        <span className="nc-notes-preview">Sylvester Asare Sarpong</span>
        <span className="nc-notes-date">{date}</span>
      </span>
    </button>
  );
};

// Notification Center, from the date and time in the menu bar: notifications, then widgets.
const NotificationCenter = ({ open, onClose, panelRef }) => {
  const items = useNotifications();

  // Like macOS, banners on screen fold into Notification Center when it opens.
  useEffect(() => {
    if (open) toast.dismiss();
  }, [open]);

  return (
    <>
      {/* Progressive blur: four layers, each blurrier and narrower, so it builds up with no edge. */}
      <div className={`nc-backdrop ${open ? "is-open" : ""}`} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <aside
        ref={panelRef}
        className={`nc-panel ${open ? "is-open" : ""}`}
        aria-label="Notification Center"
        aria-hidden={!open}
        onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="nc-header nc-item" style={{ "--i": 0 }}>
          <h2>Notification Center</h2>
          {items.length > 0 && (
            <button
              type="button"
              className="nc-round"
              onClick={notifications.clear}
              aria-label="Clear all notifications"
            >
              <XMark />
            </button>
          )}
        </div>
        <NotificationStack items={items} open={open} onClose={onClose} />
        <div className="nc-grid">
          <WeatherWidget open={open} />
          <WorldClockWidget />
          <CalendarWidget onClose={onClose} />
          <NotesWidget onClose={onClose} />
        </div>
        <div className="nc-footer nc-item" style={{ "--i": 6 }}>
          <button
            type="button"
            className="nc-pill"
            onClick={() => notify("Editing widgets is turned off on this Mac. The Notes widget opens my resume.")}
          >
            Edit Widgets
          </button>
          <button type="button" className="nc-round" onClick={onClose} aria-label="Close Notification Center">
            <XMark />
          </button>
        </div>
      </aside>
    </>
  );
};

export default NotificationCenter;
